import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { IntlIcon } from '@/components/IntlIcon';
import { Button } from '@/components/ui/button';
import {
  type DocumentOpenOutcome,
  useDocumentOpen,
} from '@/hooks/useDocumentOpen';

function assertNever(value: never): never {
  throw new Error(`Unhandled document open status: ${value}`);
}

function getErrorMessage(
  outcome: DocumentOpenOutcome,
  documentName: string,
): string | null {
  switch (outcome) {
    case 'success':
      return null;
    case 'not-found':
      return `${documentName.charAt(0).toUpperCase()}${documentName.slice(1)} not available`;
    case 'blocked':
      return `Allow pop-ups to open the ${documentName}`;
    case 'error':
      return 'Something went wrong. Try again.';
    default:
      return assertNever(outcome);
  }
}

export function DocumentOpenButton({
  documentName,
  load,
}: {
  documentName: string;
  load: () => Promise<Blob>;
}) {
  const { status, open } = useDocumentOpen(load);
  const isLoading = status === 'loading';

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      disabled={isLoading}
      title={`Open ${documentName}`}
      onClick={async () => {
        const message = getErrorMessage(await open(), documentName);
        if (message) {
          toast.error(message);
        }
      }}
      className="h-10 w-10 shrink-0 rounded-[4px] p-0"
    >
      {isLoading ? (
        <Loader2 className="h-6 w-6 animate-spin" />
      ) : (
        <IntlIcon name="launch" className="h-6 w-6" />
      )}
    </Button>
  );
}
